/* global ga */
import * as Cookies from 'js-cookie';
import { validateActionMetric, formatGaFromTwitterStyle, formatGaLegacy, } from './formatEvent';
export const defaultGetCurrentTimestamp = () => new Date().toISOString();
export const defaultGetPageId = () => null;
export const defaultGetSessionId = () => {
    const withTimestamp = Cookies.get('bsid');
    if (typeof withTimestamp === 'string') {
        return withTimestamp.split('_')[0];
    }
    else {
        return null;
    }
};
export const defaultGetTrackingId = () => Cookies.get('firstTimeVisitor') || null;
export const defaultGetUserAgent = () => typeof window !== 'undefined' && window.navigator
    ? window.navigator.userAgent
    : null;
const batchedMetrics = {};
export const defaultSend = (metric) => {
    // do not send if in server
    if (typeof window === 'undefined') {
        return;
    }
    validateActionMetric(metric);
    // -----------------------------------------------------------
    // SEND THE METRIC TO GA
    // Format the metric with legacy rules:
    const fieldsObjectLegacy = formatGaLegacy(metric);
    // Format the metric with data from twitter-style fields
    const fieldsObjectTwitterStyle = formatGaFromTwitterStyle(metric);
    if (typeof ga === 'function') {
        // send both metrics
        ga('send', fieldsObjectLegacy);
        ga('send', fieldsObjectTwitterStyle);
    }
    // -----------------------------------------------------------
    // SEND THE METRIC TO PEDL
    // Add the metric to batched metrics cache
    const metricKey = metric.BusinessUnit || 'metric-without-business-unit';
    if (batchedMetrics[metricKey]) {
        batchedMetrics[metricKey].push(metric);
    }
    else {
        batchedMetrics[metricKey] = [metric];
    }
    /*
     * Add metric to queue to be sent in batches. Uses the 'idle until
     * urgent' pattern: https://philipwalton.com/articles/idle-until-urgent/
     *
     * This will make sure events will only be sent when the application
     * is idle, at the same time guaranteeing that they will be sent
     * before page unload
     */
    const windowWithDefinedProperties = window;
    if (typeof windowWithDefinedProperties.requestIdleCallback === 'function') {
        windowWithDefinedProperties.requestIdleCallback(() => sendMetricsInQueue());
    }
    else {
        window.setTimeout(() => sendMetricsInQueue(), 1);
    }
};
function sendMetricsInQueue(sync = false) {
    for (let businessUnit in batchedMetrics) {
        if (batchedMetrics.hasOwnProperty(businessUnit)) {
            // gather events from queue
            const eventGroup = batchedMetrics[businessUnit];
            // do not send if there are no items to be sent
            if (eventGroup.length <= 0) {
                return;
            }
            // clean queue
            batchedMetrics[businessUnit] = [];
            // group the events together in the EventData array
            const eventData = eventGroup
                .map(event => event.EventData)
                .reduce((prev, curr) => prev.concat(curr), []);
            const body = Object.assign({}, eventGroup[0], { EventData: eventData });
            logForTestingPurpose(body);
            if (process.env.NODE_ENV !== 'test') {
                sendToServer(body, sync);
            }
        }
    }
}
// SEND ALL EVENTS BEFORE UNLOAD:
if (typeof window !== 'undefined') {
    /**
     * Adding a callback to 'beforeunload' disables in-memory page navigation cache,
     * which can be thought of as a "cache for the back button".
     *
     * All our pages have 'no-cache' and 'no-store' headers, and they _do not qualify for
     * such cache_.
     *
     * That's why we can use the beforeunload event without problems at Zocdoc.
     *
     * If the METRICS_SYNC_POST_ON_UNLOAD env variable is set to true, the request sent at
     * this moment will be _synchronous_, meaning the page will wait for the request to finish
     * before redirecting.
     *
     * Why would you want it to be synchronous? When _making sure 100% of the events get sent_ is
     * more important than the small performance hit incurred in page navigation. This is specially
     * important when navigating between different hosts, where we have been observing more
     * events being lost before navigation.
     * Example: Widgets redirecting to Zocdoc website.
     */
    const sync = Boolean(process.env.METRICS_SYNC_POST_ON_UNLOAD);
    window.addEventListener('beforeunload', () => sendMetricsInQueue(sync));
}
function logForTestingPurpose(metric) {
    const windowWithDefinedProperties = window;
    try {
        if (windowWithDefinedProperties && !windowWithDefinedProperties.pedl) {
            windowWithDefinedProperties.pedl = () => { };
        }
        if (windowWithDefinedProperties && windowWithDefinedProperties.pedl) {
            windowWithDefinedProperties.pedl(metric);
        }
    }
    catch (e) { }
}
function sendToServer(body, sync = false) {
    const pedlEndpoint = process.env.PEDL_ENDPOINT || '/eventslogging/v1/event';
    if (sync) {
        // Synchronous request:
        // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/Synchronous_and_Asynchronous_Requests
        try {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', pedlEndpoint, false);
            xhr.setRequestHeader('Content-Type', 'application/json');
            return xhr.send(JSON.stringify(body));
        }
        catch (e) {
            // don't stop redirecting if request fails
        }
    }
    else {
        return fetch(pedlEndpoint, {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    }
}
